import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { AccordionDetails, Box, Typography } from '@mui/material';
import moment from 'moment';
import { toast } from 'react-toastify';

import { FHAccordion, FHButton, FHConfirmDialog } from '../../../../base';
import { behaviorActions, goalActions } from '../../../../../store';
import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import GoalSummary from './GoalSummary';
import GoalDetails from './GoalDetails';
import GoalTargets from './GoalTargets';
import GoalDescriptions from './GoalDescriptions';
import {
  FORTA_ERROR,
  FORTA_GRAY3,
  FORTA_GRAY4,
  FORTA_BLUE,
  FORTA_WHITE,
} from '../../../../../utils/colors';
import { Themes } from '../../../../../constants';
import { GOAL_DETAILS_FIELD_IDS, GoalTypes, TargetStages } from '../common/constants/general';
import UnsavedIndicator from '../../components/unsavedIndicator';
import { shouldUnlinkSAGGoal, willV2GoalBeActive } from './utils/general';

import type { TreatmentPlanResourceVersion } from '../common/constants/general';
import type {
  ITreatmentPlanGoalTarget,
  ITreatmentPlanGoal,
  IBehaviorGoalName,
} from '../../../../../utils/types';
import type { RootState } from '../../../../../store';

interface GoalItemProps {
  index: number;
  values: ITreatmentPlanGoal;
  submitting?: boolean;
  targetSaving: boolean;
  errors?: any;
  goalVersion: TreatmentPlanResourceVersion;
  onUpdateGoalField: (index: number, key: string, value: any) => void;
  onSave: () => void;
  onDelete: () => void;
  onSaveTarget: (index: number, target: ITreatmentPlanGoalTarget) => void;
  onCancelGoalChange: () => void;
  trialSummary: any;
}

function GoalItem(props: GoalItemProps) {
  const {
    index,
    values: goal,
    submitting = false,
    targetSaving = false,
    errors,
    goalVersion,
    onUpdateGoalField,
    onSave,
    onDelete,
    onSaveTarget,
    onCancelGoalChange,
    trialSummary,
  } = props;

  const onUpdateField = useCallback(
    (key: string, value: any) => onUpdateGoalField(index, key, value),
    [onUpdateGoalField, index],
  );

  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isTpEditable = useTreatmentPlanEditable();
  const { current: treatmentPlan } = useSelector((state: RootState) => state.treatmentPlans);
  const { behaviorReductionGoals, skillAcquisitionGoals, caregiverGoals } = useSelector(
    (state: RootState) => state.goals,
  );
  const { goalNames: behaviorGoalNames } = useSelector((state: RootState) => state.behaviors);
  const [goalNames, setGoalNames] = useState<IBehaviorGoalName[]>([]);
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [showConfirmCancelGoal, setShowConfirmCancelGoal] = useState<boolean>(false);
  const [targetToDelete, setTargetToDelete] = useState<number | null>(null);
  const [filteredGoalNames, setFilteredGoalNames] = useState<IBehaviorGoalName[]>([]);
  const [selectedGoalNameIds, setSelectedGoalNameIds] = useState<number[]>([]);
  const [showTargetDeleteConfirm, setShowTargetDeleteConfirm] = useState<boolean>(false);
  const [showUnlinkedGoalError, setShowUnlinkedGoalError] = useState<boolean>(false);
  const [showLinkedSAGError, setShowLinkedSAGError] = useState<boolean>(false);
  const [showLinkedInActiveSAGError, setShowLinkedInActiveSAGError] = useState<boolean>(false);

  useEffect(() => {
    const behaviorId = goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id;
    if (behaviorId == null) return;

    if (behaviorGoalNames?.[behaviorId]) {
      setGoalNames(behaviorGoalNames[behaviorId]);
    } else {
      const fetchGoalNames = async () => {
        const { payload } = await dispatch(
          behaviorActions.getGoalNames({
            behaviorId,
            getAccessTokenSilently,
          }),
        );

        if (!payload) return;

        setGoalNames(payload);
      };

      fetchGoalNames();
    }
  }, [goal?.[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id]);

  useEffect(() => {
    setIsEditable(isTpEditable);
    const isActive = willV2GoalBeActive(goal);
    if (
      isActive &&
      goal?.goal_type === GoalTypes.BehaviorReduction &&
      !goal?.linked_goals?.skill_acquisition?.length
    ) {
      setShowUnlinkedGoalError(true);
    } else {
      setShowUnlinkedGoalError(false);
    }

    const showUnlinkError = shouldUnlinkSAGGoal(goal);
    if (
      showUnlinkError &&
      goal?.goal_type === GoalTypes.BehaviorReduction &&
      goal?.linked_goals?.skill_acquisition?.length
    ) {
      setShowLinkedSAGError(true);
    } else {
      setShowLinkedSAGError(false);
    }

    if (goal?.goal_type === GoalTypes.BehaviorReduction) {
      const inactiveLinkedError =
        goal?.linked_goals?.skill_acquisition?.some((g) => {
          const sagGoal = skillAcquisitionGoals.find((sg) => sg.id && sg.id === g.id);
          if (sagGoal) {
            const isSAGActive = willV2GoalBeActive(sagGoal);
            if (!isSAGActive) {
              return true;
            } else {
              return false;
            }
          }
          return false;
        }) ?? false;
      setShowLinkedInActiveSAGError(inactiveLinkedError);
    } else {
      setShowLinkedInActiveSAGError(false);
    }
  }, [isTpEditable, goal]);

  useEffect(() => {
    const existingGoalNameIds = [
      ...behaviorReductionGoals,
      ...skillAcquisitionGoals,
      ...caregiverGoals,
    ]
      .filter((g: ITreatmentPlanGoal) => g.id && g.id !== goal.id)
      .map((g: ITreatmentPlanGoal) => g[GOAL_DETAILS_FIELD_IDS.GOAL_NAME_ID]);

    setFilteredGoalNames(
      goalNames.filter((goalName: IBehaviorGoalName) => !existingGoalNameIds.includes(goalName.id)),
    );
    setSelectedGoalNameIds(existingGoalNameIds.filter((id): id is number => Number.isFinite(id)));
  }, [goal, goalNames, behaviorReductionGoals, skillAcquisitionGoals, caregiverGoals]);

  const isValidGoal = useMemo(() => {
    if (!errors) return true;

    let isValid = true;
    Object.keys(errors).forEach((key) => {
      if (key === 'targets') {
        Object.values(errors['targets']).forEach((target: any) => {
          Object.keys(target).forEach((key) => {
            if (target[key]) {
              isValid = false;
            }
          });
        });
      } else {
        if (errors[key]) {
          isValid = false;
        }
      }
    });

    return isValid;
  }, [errors]);

  const handleUpdateSummaryField = (key: string, value: any) => {
    if (key === 'is_editing' && Boolean(value)) {
      dispatch(
        goalActions.persistOriginalGoal({
          goalType: goal.goal_type,
          index,
          goal,
        }),
      );
    }

    onUpdateField(key, value);
  };

  const handleAddTarget = () => {
    if (!goal) return;

    onUpdateField('targets', [
      ...goal.targets,
      {
        label: '',
        instruction: '',
        is_event_duration_required: false,
        is_active: true,
        is_editing: true,
        expanded: true,
        dirty: true,
      },
    ]);
  };

  const handleAddTargetWithValues = (data: any) => {
    if (!goal) return;

    onUpdateField('targets', [
      ...goal.targets,
      {
        label: '',
        ...data,
      },
    ]);
  };

  const handleUpdateTargetField = (index: number, key: string, value: any) => {
    if (!goal) return;

    const dirty =
      key === 'is_active' ||
      key === 'is_editing' ||
      key === 'client_progress_expanded' ||
      key === 'expanded'
        ? (goal.targets[index] && goal.targets[index]['dirty']) || false
        : (goal.targets[index] && goal.targets[index]['dirty']) ||
          Object.entries(goal.targets[index] ?? {}).find(([field]) => field === key)?.[1] !== value;

    onUpdateField(
      'targets',
      goal.targets.map((target, i) => (index === i ? { ...target, [key]: value, dirty } : target)),
    );
  };

  const handleDeleteTarget = async (index: number) => {
    if (!treatmentPlan?.id || !goal || !goal.targets) return;
    const targets = goal.targets;
    if (!targets[index]) {
      toast.error('Target not found.');
      return;
    }

    const activeTargets = targets.filter((target) => target.is_active);
    if (targets[index]?.is_active && activeTargets.length <= 1) {
      toast.error('Goal should have at least one active target.');
      return;
    }

    setTargetToDelete(index);
    setShowTargetDeleteConfirm(true);
  };

  const handleConfirmTargetDelete = async () => {
    if (!treatmentPlan?.id || !goal || !goal.targets) return;
    if (targetToDelete === null) return;

    const targets = goal.targets;
    if (Object.hasOwn(targets[targetToDelete], 'id')) {
      const res = await dispatch(
        goalActions.removeGoalTarget({
          treatmentPlanId: treatmentPlan.id,
          goalId: goal.id,
          targetId: targets[targetToDelete].id,
          getAccessTokenSilently,
        }),
      );

      if (res.error) return;
    }

    onUpdateField(
      'targets',
      targets.filter((_, i) => i !== targetToDelete),
    );

    toast.success('Target deleted successfully.');

    setShowTargetDeleteConfirm(false);
    setTargetToDelete(null);
  };

  const handleSaveTarget = (index: number, target: ITreatmentPlanGoalTarget) => {
    if (!goal || !goal.targets) return;
    const targets = goal.targets;
    if (!targets[index]) {
      toast.error('Target not found.');
      return;
    }
    onSaveTarget(index, {
      ...target,
    });
  };

  const handleUpdateTargetActivation = async (index: number, reason: string) => {
    if (
      !treatmentPlan ||
      !goal ||
      !goal?.targets ||
      !(goal?.targets[index] != null && Object.hasOwn(goal?.targets[index], 'id'))
    )
      return;

    const targets = goal.targets;
    if (!targets[index]) {
      toast.error('Target not found.');
      return;
    }

    if (reason.trim().length < 2) {
      toast.error('Reason requires at least 2 characters.');
      return;
    }

    if (targets[index].is_active) {
      const res = await dispatch(
        goalActions.deactivateTarget({
          treatmentPlanId: treatmentPlan.id,
          goalId: goal.id,
          targetId: targets[index].id,
          data: {
            reason,
          },
          getAccessTokenSilently,
        }),
      );

      if (res.error) {
        onUpdateField(
          'targets',
          targets.map((target, i) => (index === i ? { ...target, is_active: true } : target)),
        );
        return;
      }
      toast.success('Target deactivated.');
    } else {
      const res = await dispatch(
        goalActions.activateTarget({
          treatmentPlanId: treatmentPlan.id,
          goalId: goal.id,
          targetId: targets[index].id,
          data: {
            reason,
          },
          getAccessTokenSilently,
        }),
      );

      if (res.error) {
        onUpdateField(
          'targets',
          targets.map((target, i) => (index === i ? { ...target, is_active: false } : target)),
        );
        return;
      }
      const toastId = 'targetActivatedToast';
      toast.success('Target activated.', { toastId });
    }

    onUpdateField(
      'targets',
      targets.map((target, i) =>
        index === i ? { ...target, is_active: !target.is_active, reason } : target,
      ),
    );
  };

  const handleCancelTargetChange = (index: number, prevTarget: ITreatmentPlanGoalTarget | null) => {
    if (!goal) return;

    onUpdateField(
      'targets',
      prevTarget
        ? goal.targets.map((target, i) => (index === i ? prevTarget : target))
        : goal.targets.filter((_, i) => index !== i),
    );
  };

  const handleBulkStageChange = async (
    selectedTargets: ITreatmentPlanGoalTarget[],
    selectedStage: string,
    selectedReason: string,
    selectedStageDate: string,
  ) => {
    const targetIds = selectedTargets.map((target) => target.id);
    const res = await dispatch(
      goalActions.bulkStageChange({
        treatmentPlanId: treatmentPlan?.id,
        goalId: goal.id,
        data: {
          target_ids: targetIds,
          stage: selectedStage,
          reason_for_change:
            selectedStage === TargetStages.OnHold
              ? 'On Hold - ' + selectedReason
              : selectedStage === TargetStages.Discontinued
                ? 'Discontinued - ' + selectedReason
                : null,
          date_stage_changed: moment(selectedStageDate).format('YYYY-MM-DD'),
        },
        getAccessTokenSilently,
      }),
    );

    if (!res.error) {
      const goalListMap = {
        [GoalTypes.BehaviorReduction]: behaviorReductionGoals,
        [GoalTypes.SkillAcquisition]: skillAcquisitionGoals,
        [GoalTypes.Caregiver]: caregiverGoals,
      };

      const currentGoalList = goalListMap[goal.goal_type];
      dispatch(
        goalActions.updateGoals({
          type: goal.goal_type,
          list: currentGoalList.map((g: ITreatmentPlanGoal) =>
            g.id === goal.id ? { ...res.payload, expanded: true } : g,
          ),
        }),
      );
    }
  };

  const handleCancelChange = () => {
    onUpdateField('is_editing', false);
    setShowConfirmCancelGoal(false);
    onCancelGoalChange();
  };

  const hasUnsavedChanges =
    (goal?.dirty && goal?.is_editing) || goal?.targets?.some((target) => target.dirty);

  return (
    <FHAccordion
      colorTheme={Themes.Primary}
      defaultExpanded={false}
      expanded={Boolean(goal?.expanded)}
      hasErrors={
        !isValidGoal || showUnlinkedGoalError || showLinkedSAGError || showLinkedInActiveSAGError
      }
      hasWarnings={hasUnsavedChanges}
    >
      <Box sx={{ position: 'relative' }}>
        {hasUnsavedChanges && <UnsavedIndicator />}
        <GoalSummary
          index={index}
          goal={goal}
          submitting={submitting}
          onUpdateField={handleUpdateSummaryField}
          onDelete={onDelete}
        />
      </Box>
      {goal?.expanded ? (
        <AccordionDetails sx={{ px: 3 }}>
          {goal?.is_editing && isEditable && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <FHButton
                data-testid="cancelGoalButtonTop"
                sx={{
                  backgroundColor: FORTA_WHITE,
                  border: `1px solid ${FORTA_BLUE.light3}`,
                  color: FORTA_GRAY4,
                  mr: 3,
                  '&:hover': {
                    backgroundColor: FORTA_WHITE,
                    border: `1px solid ${FORTA_BLUE.light3}`,
                    color: FORTA_GRAY4,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: FORTA_WHITE,
                    color: FORTA_GRAY3,
                  },
                }}
                disabled={submitting}
                onClick={() => setShowConfirmCancelGoal(true)}
              >
                Cancel
              </FHButton>
              <Box sx={{ position: 'relative' }}>
                {goal?.dirty && <UnsavedIndicator right="-20px" top="-15px" />}
                <FHButton
                  data-testid="saveGoalButtonTop"
                  colorTheme={Themes.Primary}
                  disabled={!isEditable || submitting}
                  onClick={onSave}
                >
                  Save Goal
                </FHButton>
              </Box>
            </Box>
          )}
          {goal?.is_editing && (
            <Typography
              color={!isValidGoal ? FORTA_ERROR : FORTA_BLUE.light1}
              mb={3}
              align="left"
              fontWeight={700}
            >
              Attention: All fields with * are required to be filled
            </Typography>
          )}
          {treatmentPlan?.id != null ? (
            <GoalDetails
              goalIndex={index}
              goal={goal}
              treatmentPlanId={treatmentPlan.id}
              goalNames={filteredGoalNames}
              selectedGoalNameIds={selectedGoalNameIds}
              errors={errors}
              onUpdateField={onUpdateField}
            />
          ) : (
            <Typography color={FORTA_ERROR} mb={3}>
              Goal details are unavailable until the treatment plan loads.
            </Typography>
          )}
          {treatmentPlan?.id != null && (
            <GoalDescriptions
              treatmentPlanId={treatmentPlan.id}
              goal={goal}
              errors={errors}
              onUpdateField={onUpdateField}
              goalIndex={index}
              goalVersion={goalVersion}
            />
          )}
          <GoalTargets
            goal={goal}
            trialSummary={trialSummary}
            targetSaving={targetSaving}
            onAddTarget={() => {
              // To keep the text field data on blur
              setTimeout(() => {
                handleAddTarget();
              });
            }}
            onUpdateField={handleUpdateTargetField}
            onSaveTarget={handleSaveTarget}
            onDeleteTarget={handleDeleteTarget}
            onAddTargetWithValues={handleAddTargetWithValues}
            onUpdateTargetActivation={handleUpdateTargetActivation}
            onCancelTargetChange={handleCancelTargetChange}
            onBulkStageChange={handleBulkStageChange}
          />
          {goal?.is_editing && isEditable && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 3 }}>
              <FHButton
                data-testid="cancelGoalButton"
                sx={{
                  backgroundColor: FORTA_WHITE,
                  border: `1px solid ${FORTA_BLUE.light3}`,
                  color: FORTA_GRAY4,
                  mr: 3,
                  height: 48,
                  '&:hover': {
                    backgroundColor: FORTA_WHITE,
                    border: `1px solid ${FORTA_BLUE.light3}`,
                    color: FORTA_GRAY4,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: FORTA_WHITE,
                    color: FORTA_GRAY3,
                  },
                }}
                disabled={submitting}
                onClick={() => setShowConfirmCancelGoal(true)}
              >
                Cancel
              </FHButton>
              <Box sx={{ position: 'relative' }}>
                {goal?.dirty && <UnsavedIndicator right="-20px" top="-15px" />}
                <FHButton
                  data-testid="saveGoalButton"
                  sx={{
                    height: 48,
                  }}
                  colorTheme={Themes.Primary}
                  disabled={!isEditable || submitting}
                  onClick={onSave}
                >
                  Save Goal
                </FHButton>
              </Box>
            </Box>
          )}
        </AccordionDetails>
      ) : null}
      <FHConfirmDialog
        open={showConfirmCancelGoal}
        colorTheme={Themes.Primary}
        title={'Cancel goal'}
        content={'Are you sure you want to cancel? Any new information will be lost.'}
        submittext={'Cancel'}
        canceltext={'Return to goal'}
        onClose={() => setShowConfirmCancelGoal(false)}
        onSubmit={handleCancelChange}
      />
      <FHConfirmDialog
        open={showTargetDeleteConfirm}
        colorTheme={Themes.Primary}
        title={'Delete target'}
        content={'Are you sure you want to delete this target? This action cannot be undone.'}
        submittext={'Delete'}
        canceltext={'Cancel'}
        onClose={() => {
          setShowTargetDeleteConfirm(false);
          setTargetToDelete(null);
        }}
        onSubmit={handleConfirmTargetDelete}
      />
    </FHAccordion>
  );
}

export default GoalItem;
